import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../utils/api';
import { CreateApplicationDialog } from './CreateApplicationDialog';

interface Application {
  id: string;
  name: string;
  created: string;  // ISO 8601 format: "2025-05-26T13:14:21.799407"
  fly_app_name: string;
  preview_url: string;
  prod_url: string;
  created_by: {
    first_name: string;
    last_name: string;
  };
}

export const ApplicationsTable: React.FC = () => {
  const api = useApi();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [appToDelete, setAppToDelete] = useState<Application | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreatingExploration, setIsCreatingExploration] = useState(false);

  const fetchApplications = async () => {
    try {
      const data = await api.get('/api/applications');
      setApplications(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleApplicationCreated = () => {
    // Refresh the applications list after creation
    fetchApplications();
  };

  const handleDeleteClick = (app: Application) => {
    setAppToDelete(app);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!appToDelete) return;

    setIsDeleting(true);
    try {
      await api.delete(`/api/applications/${appToDelete.id}`);
      setApplications(prev => prev.filter(app => app.id !== appToDelete.id));
      setShowDeleteModal(false);
      setAppToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete application');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setAppToDelete(null);
  };

  const handleCreateExploration = async () => {
    setIsCreatingExploration(true);
    try {
      const response = await api.post('/api/explorations', {});
      navigate(`/explore/${response.exploration_id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create exploration');
    } finally {
      setIsCreatingExploration(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-4 text-red-600">{error}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-3 mb-6">
        {process.env.NODE_ENV === 'development' && (
          <button
            onClick={handleCreateExploration}
            disabled={isCreatingExploration}
            className="text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 hover:border-gray-400 font-medium rounded-xl text-sm px-6 py-3 transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2 disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            {isCreatingExploration ? 'Starting...' : 'Explore an Idea'}
          </button>
        )}
        <button
          onClick={() => setShowCreateModal(true)}
          className="text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 font-medium rounded-xl text-sm px-6 py-3 transition-all duration-200 shadow-sm hover:shadow-lg flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Application
        </button>
      </div>

      {/* Create Application Dialog */}
      <CreateApplicationDialog
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onApplicationCreated={handleApplicationCreated}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && appToDelete && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-20 backdrop-blur-sm overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative p-8 w-96 shadow-2xl rounded-2xl bg-white">
            <div className="mt-2">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-50 rounded-full mb-4">
                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                Delete Application
              </h3>
              <div className="mb-6">
                <p className="text-gray-600 text-center text-sm">
                  Are you sure you want to delete <span className="font-semibold">"{appToDelete.name}"</span>? This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-center gap-3">
                <button
                  type="button"
                  onClick={handleDeleteCancel}
                  className="text-gray-600 bg-gray-100 hover:bg-gray-200 font-medium rounded-xl text-sm px-5 py-2.5 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                  className="text-white bg-red-500 hover:bg-red-600 font-medium rounded-xl text-sm px-5 py-2.5 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5">
        {applications.map((app) => (
          <div
            key={app.id}
            onClick={() => navigate(`/applications/${app.id}/edit`)}
            className="group bg-white rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-xl transition-all duration-300 p-6 flex flex-col h-48 cursor-pointer hover:scale-[1.02]"
          >
            <div className="flex-1">
              <h3 className="text-gray-900 font-medium text-base mb-1 truncate" title={app.name}>
                {app.name}
              </h3>
              <p className="text-gray-400 text-xs">Application</p>
            </div>
            <div className="flex items-center justify-between mt-auto pt-4">
              <div className="flex items-center gap-2">
                <a
                  href={app.preview_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors duration-200 hover:underline"
                >
                  Preview
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
                {app.prod_url && (
                  <a
                    href={app.prod_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-green-600 hover:text-green-700 text-sm font-medium transition-colors duration-200 hover:underline"
                  >
                    Live
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteClick(app);
                }}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200"
                title="Delete application"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}; 